/**
 * ProveWithoutRevealPanel — the zero-knowledge compliance prover game.
 *
 * EVENT-DRIVEN: a pure step function in algorithms/proveWithoutReveal.ts is
 * driven by React useState here (no requestAnimationFrame). The arcade visual
 * widgets supply the look; useArcadeScore supplies scoring.
 *
 * The game IS the theorem. The move set has no reveal-witness constructor
 * (zero-knowledge, ZKP-1) and no forge-acceptance constructor (soundness,
 * ZKP-3). On the non-compliant level, the accepting Respond move for the
 * pathless challenge is simply not generated — acceptance is unconstructable,
 * as opposed to a DENIED verdict on an attempted forgery.
 *
 * Anti-drift: FM-03 (Observation ⇒ Admissibility) is avoided — the win is not
 * "the game noticed a forgery"; the forge move never exists. FM-01/06/07 are
 * not reachable: no label, cost, or wrapper unlocks an accepting response; only
 * a held witness does.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React, { useCallback, useMemo, useState } from "react";
import {
  ArcadeActionButton,
  ArcadeArenaCard,
  ArcadeArenaStage,
  ArcadeKeyframes,
  ArcadeLogPanel,
  ArcadeMissionPrompt,
  ArcadePlayfieldFrame,
  ArcadeStatusDots,
  FlashOverlay,
  createArcadeHudStripProps,
  rankForScore,
  useArcadeActionHotkeys,
  useArcadeScore,
  useReducedMotion,
} from "../../../games/index.js";
import {
  acceptingResponseConstructable,
  activeChallenge,
  answeredChallengeCount,
  createProveWithoutRevealState,
  forgeAcceptanceMoveGenerated,
  getProveWithoutRevealLevel,
  legalProveWithoutRevealMoves,
  PROVE_WITHOUT_REVEAL_LEVELS,
  remainingChallengeCount,
  revealWitnessMoveGenerated,
  stepProveWithoutReveal,
  transcriptAccepting,
  type ProveWithoutRevealState,
} from "../algorithms/proveWithoutReveal.js";
import {
  ChallengeCard,
  ProtocolStepRail,
  proverFlashColor,
  TranscriptList,
} from "./prove-without-reveal-panel/ProverPieces.js";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
const ACCEPT_POINTS = 60;
export function ProveWithoutRevealPanel(): React.JSX.Element {
  const [state, setState] = useState<ProveWithoutRevealState>(() =>
    createProveWithoutRevealState(),
  );
  const reducedMotion = useReducedMotion();
  const score = useArcadeScore({
    hitColor: proverFlashColor("accept"),
    missColor: proverFlashColor("stall"),
  });
  const { hit, flash, reset: resetScore } = score;

  const level = useMemo(
    () => getProveWithoutRevealLevel(state.levelId),
    [state.levelId],
  );
  const challenge = useMemo(() => activeChallenge(state), [state]);
  const legalMoves = useMemo(
    () => legalProveWithoutRevealMoves(state),
    [state],
  );
  const canCommit = legalMoves.some(
    (move) => move.type === "commit" || move.type === "select",
  );
  const respondConstructable = legalMoves.some(
    (move) => move.type === "respond",
  );
  const witnessAbsentHere =
    state.phase === "committed" && !acceptingResponseConstructable(challenge);

  const reset = useCallback(
    (levelId = state.levelId): void => {
      setState(createProveWithoutRevealState(levelId));
      resetScore();
    },
    [resetScore, state.levelId],
  );

  const commit = useCallback((): void => {
    setState((current) => {
      if (current.phase !== "select") return current;
      flash(proverFlashColor("commit"));
      return stepProveWithoutReveal(current, { type: "commit" });
    });
  }, [flash]);

  const respond = useCallback((): void => {
    setState((current) => {
      const next = stepProveWithoutReveal(current, { type: "respond" });
      const accepted = next.transcript.length > current.transcript.length;
      if (accepted) {
        const bonus = next.status === "verified" ? ACCEPT_POINTS : 0;
        hit({ bonus, color: proverFlashColor("accept") });
      } else {
        flash(proverFlashColor("stall"));
      }
      return next;
    });
  }, [flash, hit]);

  const primary = useCallback((): void => {
    if (state.status !== "playing") return;
    if (state.phase === "select") commit();
    else if (state.phase === "committed" && respondConstructable) respond();
    else flash(proverFlashColor("stall"));
  }, [commit, flash, respond, respondConstructable, state.phase, state.status]);

  useArcadeActionHotkeys({
    primaryEnabled: state.status === "playing",
    onPrimary: primary,
    onReset: reset,
  });

  const promptTitle =
    state.status === "verified"
      ? "Auditor convinced — witness never disclosed"
      : witnessAbsentHere
        ? "No witness — accepting response is unconstructable"
        : state.phase === "committed"
          ? "Commitment sealed — respond to convince the auditor"
          : "Open the challenge, then commit";
  const promptColor =
    state.status === "verified"
      ? panelTokens.immune
      : witnessAbsentHere
        ? panelTokens.violation
        : panelTokens.commit;

  return (
    <PanelSurface.PanelShell
      title="Prove Without Reveal"
      subtitle="You are a prover holding a PRIVATE authority DAG. Answer the auditor's compliance challenges by building accepting transcripts — without ever revealing the witness."
      accentColor={panelTokens.response}
    >
      <ArcadeKeyframes />

      <PanelSurface.SectionHeading>Prover console</PanelSurface.SectionHeading>
      <ArcadeArenaCard>
        <ArcadeArenaStage
          hudProps={createArcadeHudStripProps({
            stats: [
              {
                label: "answered",
                value: answeredChallengeCount(state),
                color: panelTokens.immune,
              },
              {
                label: "remaining",
                value: remainingChallengeCount(state),
                color: panelTokens.challenge,
              },
              {
                label: "stalled",
                value: state.stalledIds.length,
                color: panelTokens.violation,
              },
              {
                label: "disclosed",
                value: 0,
                color: panelTokens.witness,
                title: "edges of the witness DAG disclosed — always zero",
              },
            ],
            score: score.score,
            rank: rankForScore(score.score),
            rankColor: panelTokens.accentGold,
            scoreColor: panelTokens.bright,
            borderColor: panelTokens.border,
            clock: {
              fraction:
                answeredChallengeCount(state) /
                Math.max(1, state.challenges.length),
              remaining: remainingChallengeCount(state),
              running: state.status === "playing",
            },
            timerAccent: panelTokens.response,
            timerDanger: panelTokens.violation,
            timerTrack: "rgba(154,143,206,0.18)",
            combo: score.combo,
            comboColor: panelTokens.response,
            comboMuted: panelTokens.muted,
            reducedMotion,
            scoreLabel: "proof score",
            background: "rgba(0,0,0,0.26)",
          })}
          levelSelectProps={{
            levels: PROVE_WITHOUT_REVEAL_LEVELS,
            activeLevelId: state.levelId,
            onSelect: reset,
            onReset: () => {
              reset();
            },
            accentColor: panelTokens.response,
            borderColor: panelTokens.border,
            mutedColor: panelTokens.muted,
            brightColor: panelTokens.bright,
            activeBackground: `${panelTokens.response}22`,
            background: "rgba(11,7,38,0.5)",
          }}
        >
          <ArcadePlayfieldFrame
            borderColor={panelTokens.border}
            background="rgba(11,7,38,0.9)"
            accentColor={panelTokens.response}
            secondaryColor={panelTokens.commit}
            gridSize={52}
            minHeight={460}
            header={
              <div className="flex flex-wrap items-center justify-between gap-3">
                <PanelSurface.SectionHeading>
                  Audit floor
                </PanelSurface.SectionHeading>
                <PanelSurface.Badge color={panelTokens.muted}>
                  witness stays private
                </PanelSurface.Badge>
              </div>
            }
          >
            <div className="mb-4">
              <ArcadeMissionPrompt
                label="prover"
                title={promptTitle}
                detail={state.lastEvent}
                color={promptColor}
                hotkey="Space"
                status={
                  level.compliant ? "compliant prover" : "adversarial level"
                }
              />
            </div>

            <div className="relative space-y-4">
              <ProtocolStepRail phase={state.phase} />

              {challenge ? (
                <ChallengeCard
                  challenge={challenge}
                  index={state.activeIndex}
                  total={state.challenges.length}
                  phase={state.phase}
                  stalled={state.stalledIds.includes(challenge.id)}
                />
              ) : null}

              {witnessAbsentHere ? (
                <div
                  className="rounded-xl border border-dashed p-3 text-[12px] leading-5"
                  style={{
                    borderColor: `${panelTokens.violation}66`,
                    background: `${panelTokens.violation}12`,
                    color: panelTokens.violation,
                  }}
                >
                  The Respond move is not in the legal-move set for this
                  challenge: your DAG holds no reachability path, so an
                  accepting transcript would require constructing an element of
                  an empty witness type. There is no forge-acceptance move to
                  reach for — this is ZKP-3 soundness, by construction.
                </div>
              ) : null}

              {state.status === "verified" ? (
                <div
                  className="rounded-xl border border-solid p-3 text-[12px] leading-5"
                  style={{
                    borderColor: `${panelTokens.immune}66`,
                    background: `${panelTokens.immune}12`,
                    color: panelTokens.immune,
                  }}
                >
                  Every challenge answered with a valid transcript. The auditor
                  is convinced of full compliance, and not one edge of the
                  witness DAG was disclosed.
                </div>
              ) : null}

              <FlashOverlay
                token={score.flashToken}
                color={score.flashColor}
                reducedMotion={reducedMotion}
              />
            </div>
          </ArcadePlayfieldFrame>

          <ArcadeLogPanel
            title="Transcript log"
            event={state.lastEvent}
            borderColor={panelTokens.border}
            background="rgba(11,7,38,0.7)"
            textColor={panelTokens.text}
            badges={[
              PanelSurface.badge("ZK", panelTokens.witness),
              PanelSurface.badge("no reveal", panelTokens.response),
            ]}
          >
            <div className="space-y-3">
              <ArcadeStatusDots
                items={[
                  {
                    label: "reveal-move absent",
                    active: !revealWitnessMoveGenerated(),
                  },
                  {
                    label: "forge-move absent",
                    active: !forgeAcceptanceMoveGenerated(),
                  },
                  {
                    label: "transcript accepting",
                    active: transcriptAccepting(state),
                  },
                ]}
                activeColor={panelTokens.response}
                inactiveColor={panelTokens.muted}
              />

              <TranscriptList transcript={state.transcript} />

              <div className="grid grid-cols-2 gap-2">
                <ArcadeActionButton
                  color={
                    canCommit && state.phase === "select"
                      ? panelTokens.commit
                      : panelTokens.border
                  }
                  onClick={commit}
                  ariaLabel="Commit to the hidden witness"
                  icon="C"
                >
                  Commit
                </ArcadeActionButton>
                <ArcadeActionButton
                  color={
                    respondConstructable
                      ? panelTokens.response
                      : panelTokens.border
                  }
                  onClick={respond}
                  ariaLabel="Respond to the auditor challenge"
                  icon=">"
                >
                  Respond
                </ArcadeActionButton>
              </div>
              <ArcadeActionButton
                color={panelTokens.violation}
                onClick={() => {
                  reset();
                }}
                ariaLabel="Reset Prove Without Reveal"
                icon="R"
              >
                Reset
              </ArcadeActionButton>
            </div>
          </ArcadeLogPanel>
        </ArcadeArenaStage>
      </ArcadeArenaCard>

      <PanelSurface.Callout
        title="There is no reveal move and no forge move"
        tone="info"
        color={panelTokens.witness}
      >
        Look for a "disclose an edge" button — there isn't one. Disclosure is{" "}
        <em>structurally absent</em>, not punished (ZKP-1). On the adversarial
        level, the Respond button stays inert for the pathless challenge: the
        accepting move is <em>not generated</em> because no witness exists, as
        opposed to a runtime check stamping the forgery DENIED (ZKP-3).
      </PanelSurface.Callout>
    </PanelSurface.PanelShell>
  );
}
